import { useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, FlaskConical, GitBranch, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { LogicBuilder, describeLogic, evaluate, keyFromLabel, localize, uniqueKey, type FormLocale, type FormRule, type I18nText } from "@ncp/forms";
import { Badge, Button, EmptyState, Field, Input, NativeSelect, Textarea, cn } from "@ncp/ui";
import type { FormBody } from "./api";
import { logicFields } from "./model";

interface Props {
  body: FormBody;
  onChange: (next: FormBody | ((prev: FormBody) => FormBody), opts?: { transient?: boolean }) => void;
  locale: FormLocale;
  readOnly?: boolean;
}

/** Cross-field validation rules: JsonLogic condition (fires = violation) + bilingual message + target field + severity, with a test against the sample data. */
export function RulesPanel({ body, onChange, locale, readOnly }: Props) {
  const { t } = useTranslation();
  const fields = useMemo(() => logicFields(body, locale), [body, locale]);
  const [open, setOpen] = useState<string | null>(null);
  const rules = body.rules;
  const setRules = (next: FormRule[], transient = false) => onChange({ ...body, rules: next }, { transient });

  const add = () => {
    const key = uniqueKey("rule", rules.map((r) => r.key));
    setRules([...rules, { key, when: { "==": [{ var: fields[0]?.path ?? "field" }, ""] }, message: { en: "", ar: "" }, severity: "error", fields: fields[0] ? [fields[0].path] : [] }]);
    setOpen(key);
  };

  const sample = body.sampleData ?? undefined;

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{t("admin:forms.rules.intro")}</p>
          {!readOnly ? <Button size="sm" onClick={add}><Plus /> {t("admin:forms.rules.add")}</Button> : null}
        </div>
        {rules.length === 0 ? <EmptyState icon={GitBranch} title={t("admin:forms.rules.emptyTitle")} description={t("admin:forms.rules.emptyBody")} action={!readOnly ? <Button size="sm" onClick={add}><Plus /> {t("admin:forms.rules.add")}</Button> : undefined} /> : null}
        {rules.map((rule, i) => {
          const isOpen = open === rule.key;
          const fires = sample ? evaluate(rule.when, sample) : undefined;
          const patch = (p: Partial<FormRule>, transient = true) => setRules(rules.map((r, j) => (j === i ? { ...r, ...p } : r)), transient);
          const en = typeof rule.message === "string" ? rule.message : rule.message?.en ?? "";
          const ar = typeof rule.message === "string" ? "" : rule.message?.ar ?? "";
          return (
            <div key={rule.key} className={cn("rounded-xl border bg-white/80", isOpen ? "border-aegold-300" : "border-sand-200")}>
              <button type="button" onClick={() => setOpen(isOpen ? null : rule.key)} className="flex w-full items-start gap-3 px-3 py-2.5 text-start">
                <span className={cn("mt-1 size-2.5 shrink-0 rounded-full", rule.severity === "warning" ? "bg-warning-500" : "bg-danger-500")} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-medium">{en || rule.key}</span>
                    <Badge variant="outline" size="sm" className="font-mono">{rule.key}</Badge>
                    {rule.fields?.[0] ? <Badge variant="info" size="sm" className="font-mono">{rule.fields[0]}</Badge> : null}
                    {sample !== undefined ? fires ? <Badge variant="danger" size="sm" dot>{t("admin:forms.rules.firesOnSample")}</Badge> : <Badge variant="success" size="sm" dot>{t("admin:forms.rules.passesOnSample")}</Badge> : null}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{t("admin:forms.rules.when")} {describeLogic(rule.when, fields, locale)}</p>
                </div>
                {isOpen ? <ChevronUp className="size-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="size-4 shrink-0 text-muted-foreground" />}
              </button>
              {isOpen ? (
                <div className="space-y-3 border-t border-sand-100 p-3">
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field label={t("admin:common.key")}><Input value={rule.key} readOnly={readOnly} dir="ltr" className="h-8 font-mono text-xs" onChange={(e) => { const k = keyFromLabel(e.target.value) || e.target.value; patch({ key: k }); setOpen(k); }} /></Field>
                    <Field label={t("admin:forms.rules.target")} hint={t("admin:forms.rules.targetHint")}>
                      <NativeSelect value={rule.fields?.[0] ?? ""} disabled={readOnly} className="h-8 text-xs" onChange={(e) => patch({ fields: e.target.value ? [e.target.value] : [] }, false)}>
                        <option value="">{t("admin:forms.rules.wholeForm")}</option>
                        {fields.map((f) => <option key={f.path} value={f.path}>{f.label} ({f.path})</option>)}
                      </NativeSelect>
                    </Field>
                    <Field label={t("admin:forms.rules.severity")}>
                      <NativeSelect value={rule.severity ?? "error"} disabled={readOnly} className="h-8 text-xs" onChange={(e) => patch({ severity: e.target.value as "error" | "warning" }, false)}>
                        <option value="error">{t("admin:forms.rules.error")}</option><option value="warning">{t("admin:forms.rules.warning")}</option>
                      </NativeSelect>
                    </Field>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium">{t("admin:forms.rules.condition")}</p>
                    <p className="mb-1.5 text-[11px] text-muted-foreground">{t("admin:forms.rules.conditionHint")}</p>
                    <LogicBuilder value={rule.when} onChange={(c) => patch({ when: c ?? true }, false)} fields={fields} locale={locale} />
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Field label={t("admin:forms.rules.messageEn")}><Textarea value={en} readOnly={readOnly} className="min-h-[56px] text-xs" onChange={(e) => patch({ message: { en: e.target.value, ar } })} /></Field>
                    <Field label={t("admin:forms.rules.messageAr")}><Textarea value={ar} readOnly={readOnly} dir="rtl" className="min-h-[56px] text-xs" onChange={(e) => patch({ message: { en, ar: e.target.value } })} /></Field>
                  </div>
                  <Field label={t("admin:forms.rules.explanation")} hint={t("admin:forms.rules.explanationHint")}><Input value={localize(rule.description as I18nText | undefined, "en")} readOnly={readOnly} className="h-8 text-xs" onChange={(e) => patch({ description: e.target.value ? { en: e.target.value, ar: localize(rule.description as I18nText | undefined, "ar") } : undefined })} /></Field>
                  {!readOnly ? (
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => { const next = [...rules]; [next[i - 1], next[i]] = [next[i]!, next[i - 1]!]; setRules(next); }}><ChevronUp /> {t("admin:forms.design.moveUp")}</Button>
                        <Button size="sm" variant="ghost" disabled={i === rules.length - 1} onClick={() => { const next = [...rules]; [next[i + 1], next[i]] = [next[i]!, next[i + 1]!]; setRules(next); }}><ChevronDown /> {t("admin:forms.design.moveDown")}</Button>
                      </div>
                      <Button size="sm" variant="ghost" className="text-danger-600" onClick={() => { setRules(rules.filter((_, j) => j !== i)); setOpen(null); }}><Trash2 /> {t("admin:common.delete")}</Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <aside className="space-y-3 xl:sticky xl:top-20 xl:self-start">
        <div className="rounded-xl border border-sand-200 bg-white/80 p-3 text-xs">
          <h3 className="mb-1 flex items-center gap-1 text-sm font-semibold"><FlaskConical className="size-4" /> {t("admin:forms.rules.testBench")}</h3>
          {sample ? <p className="text-muted-foreground">{t("admin:forms.rules.testBenchHint")}</p> : <p className="flex items-start gap-1 text-warning-800"><AlertTriangle className="mt-0.5 size-3.5 shrink-0" /> {t("admin:forms.rules.noSample")}</p>}
          {sample ? (
            <ul className="mt-2 space-y-1">
              {rules.map((r) => { const f = evaluate(r.when, sample); return <li key={r.key} className="flex items-center justify-between gap-2"><code className="truncate font-mono text-[11px]">{r.key}</code>{f ? <Badge variant="danger" size="sm">{t("admin:forms.rules.fires")}</Badge> : <Badge variant="success" size="sm">{t("admin:forms.rules.passes")}</Badge>}</li>; })}
            </ul>
          ) : null}
        </div>
        <div className="rounded-xl border border-sand-200 bg-white/80 p-3 text-xs text-muted-foreground">
          <h3 className="mb-1 text-sm font-semibold text-foreground">{t("admin:forms.rules.operators")}</h3>
          <p>{t("admin:forms.rules.operatorsHint")}</p>
          <ul className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-0.5 font-mono text-[11px]" dir="ltr">
            {["==", "!=", ">", "<", "in", "and / or / !", "isEmpty", "count", "sum", "matches", "any", "daysBetween", "today"].map((o) => <li key={o}>{o}</li>)}
          </ul>
        </div>
      </aside>
    </div>
  );
}
